/**
 * Counter Animation Component
 * Animates numbers when they come into view
 * Auto-detects numbers in mixed text (e.g., "5 bln SEK" -> animates "5")
 */

export function initCounter() {
    const counters = document.querySelectorAll('[data-counter]');
    
    if (counters.length === 0) return;

    const animateCounter = (element) => {
        const text = element.getAttribute('data-counter') || element.textContent;
        
        // Extract number, prefix, and suffix from text
        // Matches patterns like: "5", "5.5", "1,234", "5 000", "5 bln SEK", "$100", etc.
        const match = text.match(/^([^\d]*)(\d(?:[\d\s,]*\d)?(?:\.\d+)?)(.*)$/);
        
        if (!match) return;
        
        const [, prefix, numberStr, suffix] = match;
        
        const hasDotDecimal = numberStr.includes('.');
        const hasSpaceSeparator = /\d\s\d/.test(numberStr);
        const commaParts = numberStr.split(',');
        const hasCommaDecimal = !hasDotDecimal && commaParts.length === 2 && commaParts[1].length > 0 && commaParts[1].length <= 2;
        const hasCommaSeparator = numberStr.includes(',') && !hasSpaceSeparator && !hasCommaDecimal;

        let normalizedNumber = numberStr.replace(/\s/g, '');

        if (hasCommaDecimal) {
            normalizedNumber = normalizedNumber.replace(',', '.');
        } else {
            normalizedNumber = normalizedNumber.replace(/,/g, '');
        }

        const targetNumber = parseFloat(normalizedNumber);

        if (isNaN(targetNumber)) return;
        
        const duration = 1500; // Animation duration in ms
        const steps = 60; // Number of animation steps
        const increment = targetNumber / steps;
        const stepDuration = duration / steps;
        
        let currentNumber = 0;
        let stepCount = 0;
        
        const timer = setInterval(() => {
            stepCount++;
            currentNumber += increment;
            
            if (stepCount >= steps) {
                currentNumber = targetNumber;
                clearInterval(timer);
            }
            
            // Format number (preserve decimal places if original had them)
            const decimalSeparator = hasDotDecimal ? '.' : (hasCommaDecimal ? ',' : null);
            let formattedNumber;

            if (decimalSeparator) {
                const decimals = numberStr.split(decimalSeparator)[1]?.length || 0;
                formattedNumber = currentNumber.toFixed(decimals);
            } else {
                formattedNumber = Math.floor(currentNumber).toString();
            }

            // Add thousand separators matching the original format
            if (hasSpaceSeparator) {
                // Swedish format: use space as thousand separator
                formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
            } else if (hasCommaSeparator) {
                // English format: use comma as thousand separator
                formattedNumber = formattedNumber.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
            }

            if (hasCommaDecimal) {
                formattedNumber = formattedNumber.replace('.', ',');
            }

            element.textContent = prefix + formattedNumber + suffix;
        }, stepDuration);
    };

    // Use IntersectionObserver to trigger animation when element comes into view
    const observerOptions = {
        threshold: 0.5, // Trigger when 50% of element is visible
        rootMargin: '0px'
    };

    const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
            if (entry.isIntersecting && !entry.target.hasAttribute('data-counter-animated')) {
                entry.target.setAttribute('data-counter-animated', 'true');
                animateCounter(entry.target);
                observer.unobserve(entry.target); // Stop observing after animation
            }
        });
    }, observerOptions);

    counters.forEach(counter => {
        observer.observe(counter);
    });
}
